"use client"

import {
    ADVISORY_FIELD_ERROR_CODES,
    BLOCKING_FIELD_ERROR_CODES,
    checkoutFieldFromInputName,
    type FieldError,
    type FieldErrorCode,
    type FieldValidator
} from "@/app/(site)/_components/Atoms/Inputs/errors/fieldErrorCodes"
import { resolveFieldErrorMessage } from "@/app/(site)/_components/Atoms/Inputs/errors/fieldErrorMessages"
import {
    useFieldValidationActions,
    type FieldValidationHandle
} from "@/app/(site)/_components/Atoms/Inputs/errors/fieldValidationRegistry"
import { characterValidator } from "@/app/(site)/_components/Atoms/Inputs/errors/fieldValidators"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

export type ValidatableElement =
    | HTMLInputElement
    | HTMLSelectElement
    | HTMLTextAreaElement

export interface UseFieldValidationOptions {
    /** `shipping_city`, which also decides the wording of the required message. */
    name: string
    langCode: string
    validators?: FieldValidator[]
    /** Allowed on top of the default set in the character check, e.g. `-`. */
    allowedCharacters?: string
    countryCode?: string
    /** The character check runs on every field unless the value is not free text. */
    validateCharacters?: boolean
    /** What a native `typeMismatch` or `patternMismatch` means for this field. */
    invalidValueCode?: FieldErrorCode
    /** Placeholder values for the message, e.g. the country in the phone error. */
    messageParams?: Record<string, string | number>
    /**
     * Errors the owner recomputes every render from something other than the value — a
     * PFAS restriction on the basket, say. Held apart from the ones handed in through the
     * registry so a field can carry both at once.
     */
    externalErrors?: Array<FieldErrorCode | FieldError>
}

const nativeErrorCode = (
    validity: ValidityState,
    invalidValueCode: FieldErrorCode
): FieldErrorCode | null => {
    if (validity.valueMissing) return "value_missing"
    if (validity.tooLong) return "value_too_long"
    if (validity.typeMismatch || validity.patternMismatch || validity.badInput) {
        return invalidValueCode
    }
    if (
        validity.tooShort ||
        validity.rangeOverflow ||
        validity.rangeUnderflow ||
        validity.stepMismatch
    ) {
        return "value_invalid"
    }
    return null
}

/**
 * One source of truth for a field's errors. Validators hand back codes, this resolves them
 * to text, and the DOM only ever receives what the browser and CSS need — nothing is read
 * back out of it.
 */
export const useFieldValidation = <T extends ValidatableElement = HTMLInputElement>({
    name,
    langCode,
    validators,
    allowedCharacters,
    countryCode,
    validateCharacters = true,
    invalidValueCode = "value_invalid",
    messageParams,
    externalErrors
}: UseFieldValidationOptions) => {
    const elementRef = useRef<T>(null)
    const [validatorErrors, setValidatorErrors] = useState<FieldError[]>([])
    /** Handed in from outside the form, e.g. the address verification dialog's issues. */
    const [reportedErrors, setReportedErrors] = useState<FieldError[]>([])
    const actions = useFieldValidationActions()

    const toFieldError = useCallback(
        (code: FieldErrorCode): FieldError => ({
            code,
            message: resolveFieldErrorMessage(code, {
                langCode,
                field: checkoutFieldFromInputName(name),
                countryCode,
                params: messageParams
            })
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [langCode, name, countryCode, JSON.stringify(messageParams)]
    )

    const toFieldErrors = useCallback(
        (errors: Array<FieldErrorCode | FieldError>) =>
            errors.map(error =>
                typeof error === "string" ? toFieldError(error) : error
            ),
        [toFieldError]
    )

    // Derived rather than mirrored into state: the owner is already the source of truth.
    const declaredKey = externalErrors
        ?.map(error =>
            typeof error === "string" ? error : `${error.code}:${error.message}`
        )
        .join("|")
    const declaredErrors = useMemo(
        () => (externalErrors ? toFieldErrors(externalErrors) : []),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [declaredKey, toFieldErrors]
    )

    /**
     * Read through a ref rather than a dependency: the arrays arrive inline and the
     * closures inside them need to see the render they were built in.
     */
    const validatorsRef = useRef<FieldValidator[]>([])

    const trim = useCallback(() => {
        const element = elementRef.current
        if (!element || element.value === element.value.trim()) return

        element.value = element.value.trim()
        // Tells React, which reads the value off the event rather than off the node.
        element.dispatchEvent(new Event("input", { bubbles: true }))
    }, [])

    const failingExternalErrorCount = [...declaredErrors, ...reportedErrors].filter(
        error => !ADVISORY_FIELD_ERROR_CODES.has(error.code)
    ).length
    const validate = useCallback(() => {
        const element = elementRef.current
        if (!element) return true

        // A message left from the last run would make the element invalid before we look.
        element.setCustomValidity("")

        const codes: FieldErrorCode[] = []
        const native = nativeErrorCode(element.validity, invalidValueCode)

        if (native) {
            codes.push(native)
        } else {
            for (const validator of validatorsRef.current) {
                const code = validator(element.value, { allowedCharacters, countryCode })
                if (code && !codes.includes(code)) codes.push(code)
            }
        }

        setValidatorErrors(codes.map(toFieldError))
        return codes.length === 0 && failingExternalErrorCount === 0
    }, [
        allowedCharacters,
        countryCode,
        failingExternalErrorCount,
        invalidValueCode,
        toFieldError
    ])

    /**
     * Editing answers an advisory: whatever Loqate made of the old value says nothing about
     * the new one. Declared errors are left alone — they are recomputed every render from
     * something the edit did not touch.
     */
    const clear = useCallback(() => {
        setValidatorErrors([])
        setReportedErrors(current =>
            current.some(error => ADVISORY_FIELD_ERROR_CODES.has(error.code))
                ? current.filter(error => !ADVISORY_FIELD_ERROR_CODES.has(error.code))
                : current
        )
    }, [])

    const replaceReportedErrors = useCallback(
        (errors: Array<FieldErrorCode | FieldError>) =>
            setReportedErrors(toFieldErrors(errors)),
        [toFieldErrors]
    )

    /** Worst first: what blocks the order, then what merely doubts the value. */
    const errors = useMemo(
        () => [...validatorErrors, ...declaredErrors, ...reportedErrors],
        [validatorErrors, declaredErrors, reportedErrors]
    )

    /**
     * Write-only: the browser needs a non-empty message to hold the field invalid, and we
     * never read it back to find out what went wrong. Advisory Loqate issues stay out of
     * native validity so mobile does not swap in its own bubble text.
     */
    useEffect(() => {
        const nativeMessage =
            errors.find(error => !ADVISORY_FIELD_ERROR_CODES.has(error.code))?.message ??
            ""
        elementRef.current?.setCustomValidity(nativeMessage)
    }, [errors])

    const focus = useCallback(() => elementRef.current?.focus(), [])

    const handle = useRef<FieldValidationHandle | null>(null)

    /**
     * Refreshed after every render, never during one. Both are read at call time — from a
     * submit or a blur, long after this has run — so they always hold the latest closures.
     */
    useEffect(() => {
        validatorsRef.current = [
            ...(validateCharacters ? [characterValidator] : []),
            ...(validators ?? [])
        ]
        handle.current = {
            trim,
            validate,
            focus,
            setExternalErrors: replaceReportedErrors,
            clear
        }
    })

    useEffect(() => actions?.register(name, handle), [actions, name])

    const isBlocking = errors.some(error => BLOCKING_FIELD_ERROR_CODES.has(error.code))
    useEffect(() => {
        actions?.reportBlocking(name, isBlocking)
    }, [actions, name, isBlocking])

    return {
        elementRef,
        errors,
        hasErrors: errors.length > 0,
        /** Put on the wrapper as `data-error`: nothing styles it today, but it gives CSS
         * and the end-to-end tests the same name the message was resolved from. */
        errorCode: errors[0]?.code,
        messages: errors.map(error => error.message),
        validate,
        clear,
        trim
    }
}
